import { EmptyResource, Resource, ResourceType, Router, Store } from 'json-api-hooks';
import { DefaultValues, FieldValues } from 'react-hook-form';

import { formatDateTime } from '../../../lib/date';
import { getFromStorage } from '../../../lib/storage';
import { isString } from '../../../lib/typeCheckers';
import { FormFieldAttribute, FormFieldDefinition } from '../ResourceForm';
import { InputType } from '../types';

const isHasMany = (relation: EmptyResource | EmptyResource[]): relation is EmptyResource[] => Array.isArray(relation);

const resourceValue = (resource: Resource | undefined, formField: FormFieldDefinition, storageKey?: string) => {
  const valueFromStorage = storageKey ? getFromStorage(storageKey) : undefined;
  if (valueFromStorage !== undefined && valueFromStorage !== null) {
    return JSON.parse(valueFromStorage);
  }

  const { attribute, multiple, type } = formField;

  if (attribute.endsWith('Id')) {
    const relation = resource?.relationships?.[attribute.slice(0, -'Id'.length)]?.data;
    if (relation && !isHasMany(relation)) {
      return relation.id;
    } else {
      return '';
    }
  }
  if (attribute.endsWith('Ids')) {
    const relation = resource?.relationships?.[`${attribute.slice(0, -'Ids'.length)}s`]?.data;
    if (relation && isHasMany(relation)) {
      return relation.map(({ id }) => id);
    } else {
      return [];
    }
  }

  const attr = resource?.attributes?.[attribute];
  if (type === InputType.DateTime && isString(attr)) {
    return formatDateTime(attr, "yyyy-MM-dd'T'HH:mm");
  } else if (type === InputType.Checkbox) {
    return attr ?? false;
  }

  if (attr !== undefined) {
    return attr;
  }

  if (multiple || [InputType.Checkboxes].includes(formField.type)) {
    return [];
  }

  return '';
};

const nestedResourceValues = (
  store: Store,
  router: Router,
  resource: Resource | undefined,
  formFields: FormFieldDefinition[],
  storageKey?: string,
) => {
  if (!resource) {
    return {};
  }

  return formFields.reduce<DefaultValues<FieldValues>>(
    (acc, formField) => {
      acc[formField.attribute] = getDefaultValue(
        store,
        router,
        resource,
        formField,
        `${storageKey}-${formField.attribute}`,
      );

      return acc;
    },
    { id: resource.id },
  );
};

const nestedValues = (
  store: Store,
  router: Router,
  resource: Resource | undefined,
  attribute: string,
  multiple: boolean | undefined,
  formFields: FormFieldDefinition[],
  storageKey?: string,
) => {
  const relation = resource?.relationships?.[attribute]?.data;

  if (!relation) {
    return multiple ? [] : '';
  }

  if (isHasMany(relation)) {
    return relation.map((nested) =>
      nestedResourceValues(store, router, store.getResource(router.resourceRoute(nested)), formFields, storageKey),
    );
  }

  return nestedResourceValues(store, router, store.getResource(router.resourceRoute(relation)), formFields, storageKey);
};

const nestedValue = <T extends ResourceType>(
  store: Store,
  router: Router,
  resource: Resource | undefined,
  formField: FormFieldDefinition,
  storageKey?: string,
): string => {
  const [relationKey, attributeKey] = formField.attribute.split('Attributes.');
  const relation = resource?.relationships?.[relationKey]?.data;

  if (!relation || isHasMany(relation)) {
    return '';
  }

  return getDefaultValue(
    store,
    router,
    store.getResource(router.resourceRoute(relation)),
    { ...formField, attribute: attributeKey as T extends void ? string : FormFieldAttribute<T> },
    `${storageKey}-${formField.attribute}`,
  ) as string;
};

const getDefaultValue = (
  store: Store,
  router: Router,
  resource: Resource | undefined,
  formField: FormFieldDefinition,
  storageKey?: string,
) => {
  const { nestedFields } = formField;

  if (nestedFields) {
    return nestedValues(
      store,
      router,
      resource,
      formField.attribute.slice(0, -'Attributes'.length),
      formField.multiple,
      nestedFields,
      storageKey,
    );
  } else if (formField.attribute.includes('Attributes.')) {
    return nestedValue(store, router, resource, formField, storageKey);
  } else {
    return resourceValue(resource, formField, storageKey);
  }
};

export default getDefaultValue;
